import React from 'react';
import { Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DestinationConfig } from '../types';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { useResolveTranslation } from '../../../common/hooks/use-resolve-translation.hook';

interface DestinationNodeProps {
  config: DestinationConfig;
  onConfigChange: (config: DestinationConfig) => void;
  onSave: () => void;
  mappedTargetFields?: string[];
}

// Radix Select rejects '' as an item value, so "no default family" gets a sentinel.
const FAMILY_NONE = '__none__';

/** A labelled field inside the node config panel. */
const ConfigField = ({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) => (
  <div>
    <Label htmlFor={htmlFor} className="mb-1 block text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const DestinationNode: React.FC<DestinationNodeProps> = ({
  config,
  onConfigChange,
  onSave,
  mappedTargetFields = [],
}) => {
  const { t } = useTranslation();
  const { rt } = useResolveTranslation();

  // Available families for the Default Family dropdown (only used in
  // create_and_update mode so drafts get a sensible family stamp).
  const availableFamilies = useSelector(
    (state: VendorState) => state.productFamilies.families || []
  );

  // Derive effective sync fields: if not explicitly set, all mapped target fields are selected
  const effectiveSyncFields = config.syncFields ?? mappedTargetFields;

  const handleToggleField = (field: string) => {
    const current = new Set(effectiveSyncFields);
    if (current.has(field)) {
      current.delete(field);
    } else {
      current.add(field);
    }
    onConfigChange({ ...config, syncFields: Array.from(current) });
  };

  const handleToggleAll = () => {
    const allSelected = mappedTargetFields.length > 0 &&
      mappedTargetFields.every(f => effectiveSyncFields.includes(f));
    onConfigChange({
      ...config,
      syncFields: allSelected ? [] : [...mappedTargetFields],
    });
  };

  const allSelected = mappedTargetFields.length > 0 &&
    mappedTargetFields.every(f => effectiveSyncFields.includes(f));
  const someSelected = effectiveSyncFields.length > 0 && !allSelected;

  return (
    <div className="flex flex-col gap-4 pt-3">
      <ConfigField label={t('PAGES.API_INTEGRATION.MATCH_FIELD')}>
        <Select
          value={config.matchField}
          onValueChange={(value) => onConfigChange({ ...config, matchField: value as DestinationConfig['matchField'] })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sku">{t('PAGES.API_INTEGRATION.MATCH_FIELDS.SKU')}</SelectItem>
            <SelectItem value="name">{t('PAGES.API_INTEGRATION.MATCH_FIELDS.NAME')}</SelectItem>
          </SelectContent>
        </Select>
      </ConfigField>

      {/* Default Family — only relevant in create_and_update mode. Stamped on
          every product CREATED by the sync so the V3 editor can open the draft
          without the vendor having to pick a family first. The vendor can
          still change it per-product before publishing. */}
      {config.syncBehavior === 'create_and_update' && (
        <div data-testid="default-family-select">
          <ConfigField label={t('PAGES.API_INTEGRATION.DEFAULT_FAMILY_LABEL', 'Default family for created products')}>
            <Select
              value={config.defaultFamilySlug ?? FAMILY_NONE}
              onValueChange={(value) =>
                onConfigChange({
                  ...config,
                  defaultFamilySlug: value === FAMILY_NONE ? undefined : value,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FAMILY_NONE}>
                  <span className="italic text-muted-foreground">
                    {t('PAGES.API_INTEGRATION.DEFAULT_FAMILY_NONE', 'None — vendor chooses per product')}
                  </span>
                </SelectItem>
                {availableFamilies.map((family: any) => (
                  <SelectItem key={family._id} value={family.slug}>
                    {rt(family.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ConfigField>
          <p className="mt-1 text-[10px] leading-normal text-muted-foreground">
            {t(
              'PAGES.API_INTEGRATION.DEFAULT_FAMILY_HINT',
              'New products are created as drafts and hidden from the catalog/visualizer until you review and publish them.',
            )}
          </p>
        </div>
      )}

      {/* Sync Fields Selection */}
      {mappedTargetFields.length > 0 && (
        <div data-testid="sync-fields-section">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('PAGES.API_INTEGRATION.SYNC_FIELDS')}
            </span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {effectiveSyncFields.length} / {mappedTargetFields.length}
            </span>
          </div>
          <div className="overflow-hidden rounded-lg border">
            {/* Select all row — checkboxes stay handler-less so the row's own
                click is the single toggle (clicks on the box bubble up). */}
            <div
              data-testid="sync-field-toggle-all"
              onClick={handleToggleAll}
              className="flex cursor-pointer items-center gap-1.5 border-b bg-muted px-1.5 py-1 transition-colors hover:bg-accent"
            >
              <Checkbox checked={someSelected ? 'indeterminate' : allSelected} />
              <span className="text-xs font-medium text-muted-foreground">
                {t('PAGES.API_INTEGRATION.SELECT_ALL')}
              </span>
            </div>
            {/* Field rows */}
            {mappedTargetFields.map((field, idx) => {
              const isChecked = effectiveSyncFields.includes(field);
              return (
                <div
                  key={field}
                  data-testid={`sync-field-${field}`}
                  onClick={() => handleToggleField(field)}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 px-1.5 py-1 transition-colors hover:bg-accent',
                    idx < mappedTargetFields.length - 1 && 'border-b',
                  )}
                >
                  <Checkbox checked={isChecked} />
                  <span
                    className={cn(
                      'font-mono text-xs',
                      isChecked ? 'font-medium text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {field}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-1 flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={onSave}>
          <Check className="size-3.5" />
          {t('PAGES.API_INTEGRATION.SAVE')}
        </Button>
      </div>
    </div>
  );
};
